'use client';

import {
  type ChangeEvent,
  type ChangeEventHandler,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
} from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { clsx } from 'clsx';
import InfoIcon from '../../../icons/react/icon-info';
import { HorizontalStack, type HorizontalStackAlign } from '../../alignment/horizontal-stack';
import { VerticalStack, type VerticalStackAlign } from '../../alignment/vertical-stack';
import { PlainIconButton } from '../../button/plain-icon/plain-icon-button';
import { Tooltip } from '../../tooltip/tooltip';
import { Typography, type TypographyTone } from '../../typography';
import { useAggregateErrorContext } from '../aggregate-error-context';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import inputStyles from '../input.module.scss';
import styles from './switch-input.module.scss';

export type SwitchInputLabelSide = 'left' | 'right';

export interface SwitchInputProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onChange'> {
  align?: HorizontalStackAlign;
  belowInputContent?: ReactNode;
  checked?: boolean;
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  info?: ReactNode;
  infoTone?: TypographyTone;
  knobIcon?: ReactNode;
  label: ReactNode;
  labelSide?: SwitchInputLabelSide;
  name: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLButtonElement>;
  required?: boolean;
  verticalAlign?: VerticalStackAlign;
}

export function SwitchInput({
  align,
  belowInputContent,
  className,
  checked,
  disabled,
  error,
  hideLabel,
  hint,
  id: providedId,
  info,
  infoTone,
  knobIcon,
  label,
  labelSide = 'left',
  name,
  onChange,
  ref,
  required,
  verticalAlign,
  ...rest
}: SwitchInputProps) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const defaultRef = useRef<HTMLButtonElement>(null);
  const resolvedRef = ref || defaultRef;

  const errorContext = useAggregateErrorContext();
  const { setErrors } = errorContext || {};

  useEffect(() => {
    if (setErrors) {
      setErrors(id, ...(error ? [error] : []));
    }

    return () => {
      if (setErrors) {
        setErrors(id);
      }
    };
  }, [setErrors, id, error]);

  const showError = Boolean(!errorContext && error);

  const handleCheckChange = useCallback(
    (newChecked: boolean) => {
      onChange?.({ target: { checked: newChecked, name, value: newChecked } } as unknown as ChangeEvent<HTMLInputElement>);
    },
    [name, onChange],
  );

  const labelElement = (
    <Typography
      as="label"
      className={clsx(styles.label, hideLabel && styles.isHidden)}
      htmlFor={id}
      lineHeight="1.25rem"
      size="0.875rem"
      wrap="break-word"
    >
      {label}
      {required ? <Typography tone="danger-dk">*</Typography> : null}
    </Typography>
  );

  const infoTooltipElement = info ? (
    <Tooltip
      activator={
        <PlainIconButton tone={infoTone}>
          <InfoIcon aria-hidden height={16} />
        </PlainIconButton>
      }
      maxWidth={140}
      side="top"
      size="small"
    >
      {info}
    </Tooltip>
  ) : null;

  return (
    <VerticalStack
      align={verticalAlign}
      className={clsx(inputStyles.input, styles.switchInput, disabled && inputStyles.isDisabled, disabled && styles.isDisabled, className)}
      gap={2}
    >
      <HorizontalStack align={align} blockAlign="center" gap={4} wrap="nowrap">
        {labelSide === 'left' && labelElement}

        <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
          {labelSide === 'left' ? infoTooltipElement : null}

          <SwitchPrimitive.Root
            checked={checked}
            className={clsx(styles.track, checked && styles.isOn)}
            disabled={disabled}
            id={id}
            name={name}
            onCheckedChange={handleCheckChange}
            ref={resolvedRef}
            required={required}
            {...rest}
          >
            <SwitchPrimitive.Thumb className={styles.knob}>
              {knobIcon ? <span className={styles.knobIcon}>{knobIcon}</span> : null}
            </SwitchPrimitive.Thumb>
          </SwitchPrimitive.Root>

          {labelSide === 'right' ? infoTooltipElement : null}
        </HorizontalStack>

        {labelSide === 'right' && labelElement}
      </HorizontalStack>

      {hint ? <InputHint id={hintId}>{hint}</InputHint> : null}
      {showError ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}

      {belowInputContent}
    </VerticalStack>
  );
}
